
/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import {GenericObject} from '@ion/core/src/types/entities';
import {CachedItem} from '../types/types';

/**
 * Defines a utility to return the key for the asset added to the treeNode
 *
 * ```typescript
 *
 * import {generateItemKey} from '@ion/dls/src/utils/';
 *
 * const pos = generateItemKey(children, key);
 *
 * ```
 */
export const generateItemKey = (children: CachedItem[], parentKey: string) =>
  children.length ? `${parentKey}-${children.length}` : `${parentKey}-0`;

/**
 * @deprecated Please use generateItemKey
 */
export const generateAssetKey = generateItemKey;

/**
 * Defines a utility to return the key for the loader
 *
 * ```typescript
 *
 * import {getTreeAssetLoaderKey} from '@ion/dls/src/utils/Keys';
 *
 * const loaderKey = getTreeAssetLoaderKey(key);
 *
 * ```
 */
export const getTreeAssetLoaderKey = (key: string) => {
  return key?.replace('-1000', '');
};

/**
 * Provides a utility which generates a key based on the key of
 * the last child within the children array, incrementing the
 * returned key by one. If the children array does not contain
 * any elements, the parent key will be used as a fallback when
 * creating the key.
 *
 * ```typescript
 *
 * import {generateKey} from '@ion/dls/src/utils/';
 *
 * const insertedKey = generateKey(children, account);
 *
 * ```
 */
export const generateKey = (children: any, account: any) => {
  if (children?.length) {
    const lastKey = children[children.length - 1].key;
    const index   = lastKey?.lastIndexOf('-');
    if (index > -1) {
      const n = lastKey.length;
      return `${lastKey.substr(0, index)}-${parseInt(lastKey.substr(index + 1, n)) + 1}`;
    }
  }
  return `${account.key}-0`;
};

/**
 * Provides a utility which returns all child keys.
 * If child has a children, this function will be invoked recursively
 * to return an array of keys
 *
 * ```typescript
 *
 * import {getAllChildKeys} from '@ion/dls/src/utils/';
 *
 * const recursiveKeys = getAllChildKeys([key], children);
 *
 * ```
 */
export const getAllChildKeys = (initialValue = [], children: {key: string, children: any}[]) => {
  let data = null;
  if (children?.length) {
    data = children.reduce((acc, child) => {
      acc.push(child.key);
      if (child?.children?.length) {
        getAllChildKeys(acc, child?.children);
      }
      return acc;
    }, initialValue);
  }
  return data ?? initialValue;
};

/**
 * Provides a utility which returns the current key
 *
 * ```typescript
 *
 * import {getCurrentKeys} from '@ion/dls/src/utils/';
 *
 *   const updatedExpandedKeys = getCurrentKeys(
 *      expandedKeys,
 *      (nextKey: string) => !recursiveKeys.includes(nextKey)
 *     );
 *
 * ```
 */
export const getCurrentKeys = (expandedKeys: string[], func: any) => {
  return expandedKeys?.filter(func);
};

/**
 * Provides a utility which returns the parent key.
 *
 * ```typescript
 *
 * import {getParentKey} from '@ion/dls/src/utils/';
 *
 * const parentKey = getParentKey(keysRef.current, item);
 *
 * ```
 */
export const getParentKey = (keys: GenericObject, item: GenericObject) => {
  return Object.keys(keys)?.find(key => keys[key]?.toString() === item?.id);
};

/**
 * Provides a utility which returns the children keys.
 *
 * ```typescript
 *
 * import {getChildrenKeys} from '@ion/dls/src/utils/';
 *
 * const childrenKeys = getChildrenKeys(keysRef.current, parentKey);
 *
 * ```
 */
export const getChildrenKeys = (keys: GenericObject, parentKey: string) => {
  return Object.keys(keys)?.filter(k => k.toString().startsWith(`${parentKey}-`));
};

/**
 * Provides a utility which returns the individual asset keys
 * based on the cached assets and parent key.
 *
 * ```typescript
 *
 * import {getIndividualAssetKeys} from '@ion/dls/src/utils/';
 *
 * const individualAssetKeys = getIndividualAssetKeys(cachedAssets, parentKey);
 *
 * ```
 */
export const getIndividualAssetKeys = (cachedAssets: GenericObject, parentKey: string) => {
  const values = Object.values(cachedAssets);
  return values?.filter?.(item => item?.key.startsWith(parentKey))?.map?.(item => item.key);
};
